import React from 'react';
import type { TimetableDto, TimetableConflict } from '../../api/axios';
import { formatClassLevelLabel } from '../../utils/classLevelLabel';
import styles from './TimetableGrid.module.css';

export interface SubjectColor {
  bg: string;
  border: string;
  text: string;
}

interface TimetableCardProps {
  entry: TimetableDto;
  conflicts: TimetableConflict[];
  isDragging: boolean;
  readOnly: boolean;
  school?: string;
  getSubjectColor: (name: string | undefined) => SubjectColor;
  onClick: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}

const formatTeacherLabel = (name?: string | null): string => {
  const base = name && name.trim().length > 0 ? name : 'No Teacher';
  const [firstPart] = base.split(/[-–]/);
  return firstPart.trim();
};

const TimetableCard: React.FC<TimetableCardProps> = ({
  entry,
  conflicts,
  isDragging,
  readOnly,
  school,
  getSubjectColor,
  onClick,
  onDragStart,
  onDragEnd,
}) => {
  const hasConflicts = conflicts.length > 0;
  const color = getSubjectColor(entry.subject?.name);

  const classNames = [
    styles.card,
    isDragging ? styles.dragging : '',
    hasConflicts ? styles.conflict : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classNames}
      style={{
        background: hasConflicts ? undefined : color.bg,
        borderLeftColor: hasConflicts ? undefined : color.border,
        color: hasConflicts ? '#92400e' : color.text,
      }}
      draggable={!readOnly}
      onClick={onClick}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      {hasConflicts && <div className={styles.conflictBadge}>!</div>}
      <div className={styles.cardSubject}>
        {entry.subject?.name || 'No Subject'}
      </div>
      <div className={styles.cardTeacher}>
        {formatTeacherLabel(entry.teacher?.name)}
        {entry.room ? ` - ${entry.room}` : ''}
      </div>
      {entry.form && (
        <div className={styles.cardRoom}>
          {formatClassLevelLabel(school, entry.form)}
        </div>
      )}
    </div>
  );
};

export default TimetableCard;
